// V3Gate.jsx — login gate for V3 (no auth_bridge, no IP whitelist).
// User logs in with their Kite API Key + Access Token + a shared password
// (see v3_users.py on the API — closes the gap where stolen Kite credentials
// alone were enough to take this over). If someone else is already active on
// this link, the user is offered two paths: join their live session (password
// only, no Kite creds needed) or switch to their own account (their own Kite
// creds + the CURRENT password). The API validates everything, starts the
// dedicated KiteConnect + KiteTicker, and returns an 8-hour session token.
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = "https://tmxapiv3.trademaxx.online";
const TOKEN_KEY = "v3_token";
// Set right before a reload triggered by an account-switch kick, so the freshly
// reloaded login page can show WHY the user landed back here (survives the
// reload; sessionStorage, not localStorage, so it doesn't linger across tabs).
const KICKED_REASON_KEY = "v3_kicked_reason";

function applyAuthHeader(token) {
    if (token) axios.defaults.headers.common["Authorization"] = "Bearer " + token;
    else delete axios.defaults.headers.common["Authorization"];
}
applyAuthHeader(localStorage.getItem(TOKEN_KEY));

// On any 401 -> clear + back to login. If the server says ACCOUNT_SWITCHED
// specifically (someone else logged in with different credentials), remember
// that so the login page can explain it.
axios.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err && err.response && err.response.status === 401) {
            const serverErr = err.response.data && err.response.data.error;
            if (serverErr === "ACCOUNT_SWITCHED") {
                try { sessionStorage.setItem(KICKED_REASON_KEY, "account_switched"); } catch (e) {}
            }
            localStorage.removeItem(TOKEN_KEY);
            applyAuthHeader(null);
            if (!window.__v3_relogging) { window.__v3_relogging = true; window.location.reload(); }
        }
        return Promise.reject(err);
    }
);

export default function V3Gate({ children }) {
    const [ready, setReady] = useState(false);
    const [authed, setAuthed] = useState(false);
    const [apiKey, setApiKey] = useState("");
    const [accessToken, setAccessToken] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    // Set only when this tab landed back on the login page because a DIFFERENT
    // Kite account logged in elsewhere (see _require_bridge / session_check
    // "account_switched" reason on the API). Shown once, then cleared.
    const [kickedReason, setKickedReason] = useState(null);
    // Whether someone else is CURRENTLY active on this link (from /active_status,
    // never reveals WHO — just yes/no). null = still checking.
    const [activeElsewhere, setActiveElsewhere] = useState(null);
    // 'join' = just enter the current password, no Kite creds needed.
    // 'switch' = my own Kite creds + the current password.
    // null = haven't chosen yet (show the two-button chooser).
    const [mode, setMode] = useState(null);

    const checkActiveStatus = useCallback(() => {
        axios.get(`${API}/active_status`)
            .then((r) => setActiveElsewhere(!!(r.data && r.data.active)))
            .catch(() => setActiveElsewhere(false));
    }, []);

    useEffect(() => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) {
            // No token at all — still check whether we just got kicked (e.g. a
            // background request 401'd and reloaded before this mount).
            try {
                const kicked = sessionStorage.getItem(KICKED_REASON_KEY);
                if (kicked) { setKickedReason(kicked); sessionStorage.removeItem(KICKED_REASON_KEY); }
            } catch (e) {}
            checkActiveStatus();
            setReady(true);
            return;
        }
        applyAuthHeader(token);
        axios.get(`${API}/session_check`)
            .then((r) => {
                if (r.data.logged_in) {
                    setAuthed(true);
                } else {
                    localStorage.removeItem(TOKEN_KEY);
                    applyAuthHeader(null);
                    if (r.data.reason === "account_switched") {
                        setKickedReason("account_switched");
                    } else {
                        try {
                            const kicked = sessionStorage.getItem(KICKED_REASON_KEY);
                            if (kicked) { setKickedReason(kicked); sessionStorage.removeItem(KICKED_REASON_KEY); }
                        } catch (e) {}
                    }
                    checkActiveStatus();
                }
            })
            .catch(() => checkActiveStatus())
            .finally(() => setReady(true));
    }, [checkActiveStatus]);

    const doLogin = useCallback(async (e) => {
        if (e) e.preventDefault();
        setError(""); setKickedReason(null); setBusy(true);
        try {
            const body = mode === 'join'
                ? { password: password.trim() }
                : { api_key: apiKey.trim(), access_token: accessToken.trim(), password: password.trim() };
            const r = await axios.post(`${API}/login`, body);
            const token = r.data && r.data.token;
            if (!token) throw new Error("No token returned");
            localStorage.setItem(TOKEN_KEY, token);
            applyAuthHeader(token);
            setAuthed(true);
        } catch (err) {
            const msg = (err.response && err.response.data && err.response.data.message)
                || "Login failed. Check your details and try again.";
            setError(msg);
        } finally { setBusy(false); }
    }, [apiKey, accessToken, password, mode]);

    // Logout: ends the ENTIRE shared session, every device signed out, per the
    // password-gated design (2026-08-05) — no longer "just this device".
    const doLogout = useCallback(async () => {
        try { await axios.post(`${API}/logout`); } catch (e) { /* ignore */ }
        try { localStorage.removeItem(TOKEN_KEY); } catch (e) { /* ignore */ }
        applyAuthHeader(null);
        window.location.reload();
    }, []);

    if (authed) return (
        <>
            <button onClick={doLogout} title="Logout (ends the session for ALL devices)" style={S.floatLogout}>
                Logout
            </button>
            {children}
        </>
    );
    if (!ready || activeElsewhere === null) return <div style={S.center}><div style={S.muted}>Loading…</div></div>;

    // Someone else is currently active and we haven't chosen a path yet.
    if (activeElsewhere && mode === null) {
        return (
            <div style={S.center}>
                <div style={S.card}>
                    <div style={S.title}>TradeMax 3T V3</div>
                    <div style={S.sub}>Someone is already using this link. What would you like to do?</div>
                    {kickedReason === "account_switched" ? (
                        <div style={S.kicked}>
                            Someone else logged in with different credentials on this link.
                            You've been signed out.
                        </div>
                    ) : null}
                    <button style={S.btn} onClick={() => { setError(""); setMode('join'); }}>
                        Just use it (enter the current password)
                    </button>
                    <button style={{ ...S.btn, background: "#2a323a", color: "#fff" }}
                            onClick={() => { setError(""); setMode('switch'); }}>
                        Switch to my own account
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={S.center}>
            <form onSubmit={doLogin} style={S.card}>
                <div style={S.title}>TradeMax 3T V3 — Sign in</div>
                <div style={S.sub}>
                    {mode === 'join'
                        ? "Enter the current password to join the active session."
                        : "Enter your Kite API Key, Access Token, and password. No auth bridge needed — works on any network/device."}
                </div>
                {kickedReason === "account_switched" ? (
                    <div style={S.kicked}>
                        Someone else logged in with different credentials on this link.
                        You've been signed out — please log in again.
                    </div>
                ) : null}
                {activeElsewhere ? (
                    <button type="button" style={S.backLink} onClick={() => { setError(""); setMode(null); }}>
                        ← back
                    </button>
                ) : null}
                {mode !== 'join' && (
                    <>
                        <label style={S.lbl}>API Key</label>
                        <input style={S.input} placeholder="Kite API Key" autoFocus autoComplete="off"
                               value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
                        <label style={S.lbl}>Access Token</label>
                        <input style={S.input} placeholder="Kite Access Token" autoComplete="off"
                               value={accessToken} onChange={(e) => setAccessToken(e.target.value)} />
                    </>
                )}
                <label style={S.lbl}>{mode === 'switch' ? "Current Password (to switch accounts)" : "Password"}</label>
                <input style={S.input} type="password" placeholder="Password" autoComplete="off"
                       autoFocus={mode === 'join'}
                       value={password} onChange={(e) => setPassword(e.target.value)} />
                {error ? <div style={S.err}>{error}</div> : null}
                <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} disabled={busy} type="submit">
                    {busy ? "Validating…" : (mode === 'join' ? "Join" : "Login")}
                </button>
            </form>
        </div>
    );
}

const S = {
    center: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0b0e11", fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif" },
    card: { width: 360, background: "#161a1e", border: "1px solid #232a31", borderRadius: 12, padding: 28, display: "flex", flexDirection: "column", gap: 8, boxShadow: "0 8px 40px rgba(0,0,0,0.4)" },
    title: { color: "#fff", fontSize: 20, fontWeight: 700, textAlign: "center" },
    sub: { color: "#8b97a3", fontSize: 12, textAlign: "center", marginBottom: 10, lineHeight: 1.4 },
    kicked: { color: "#f0b90b", fontSize: 12, textAlign: "center", background: "rgba(240,185,11,0.1)", border: "1px solid rgba(240,185,11,0.35)", borderRadius: 8, padding: "8px 10px", marginBottom: 6, lineHeight: 1.4 },
    lbl: { color: "#8b97a3", fontSize: 11, marginTop: 4, textTransform: "uppercase", letterSpacing: 0.5 },
    input: { background: "#0b0e11", border: "1px solid #2a323a", borderRadius: 8, padding: "11px 12px", color: "#fff", fontSize: 14, outline: "none" },
    btn: { background: "#f0b90b", color: "#11161b", border: "none", borderRadius: 8, padding: "12px", fontSize: 15, fontWeight: 700, cursor: "pointer", marginTop: 10 },
    err: { color: "#ff6b6b", fontSize: 13, textAlign: "center", marginTop: 4 },
    note: { color: "#5d6873", fontSize: 11, textAlign: "center", marginTop: 8, lineHeight: 1.4 },
    muted: { color: "#8b97a3", fontSize: 14 },
    floatLogout: { position: "fixed", top: 8, right: 10, zIndex: 99999, background: "#2a323a", color: "#fff", border: "1px solid #3a444e", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", opacity: 0.85 },
    backLink: { background: "none", border: "none", color: "#8b97a3", fontSize: 12, cursor: "pointer", textAlign: "left", padding: 0, marginBottom: 4 },
};
